import { useMemo } from "react";
import type { Role } from "../types/RolesPermissions.type";
import { useRoles } from "./useUserAssignments";

export interface UseAssignableRolesOptions {
	/**
	 * Skip the request entirely for a surface that is not open yet (e.g. deriving rule 6 for a
	 * modal nobody has opened). Defaults to `true`, matching `useRoles`.
	 */
	enabled?: boolean;
}

export interface UseAssignableRolesResult {
	/** Roles the direct-add flow may actually grant to EVERY target user. */
	roles: Role[];
	isLoading: boolean;
	error: unknown;
}

/**
 * PRO-757 A2 — "which roles can this Add flow actually grant to these users?", asked once.
 *
 * Rule 6 ("Add unavailable", CONTRACT §5) was being decided in two places from two different
 * questions: `UserListPage` asked `useRoles([id])` unfiltered to enable/disable Add, while
 * `AccessModeSelector` — the screen behind that button — asked
 * `useRoles(ids, { excludeEventScoped: true })` AND additionally dropped `is_event_scoped`
 * rows client-side. A user holding every non-event-scoped role company-wide therefore saw an
 * enabled Add button that led straight to "access-mode-no-roles-available", and the two
 * queries cost two `GET /rbac/roles` calls under different react-query keys.
 *
 * Both layers of CONTRACT §5.1's deliberate defence in depth live here, unchanged and
 * together: the server-side `exclude_event_scoped` param AND the client-side
 * `is_event_scoped !== true` filter. `!== true` (not `!role.is_event_scoped`) is deliberate —
 * an `is_event_scoped` that is `undefined` (an API build that has not shipped the flag yet)
 * must filter NOTHING, not everything.
 *
 * Deliberately NOT applied to the Role Management admin page's own roles list (`rbac-api.ts`'s
 * separate `getRoles`), which must keep seeing every role, event-scoped included, to edit its
 * matrix.
 */
export const useAssignableRoles = (
	assignableToUserIds?: number[],
	options?: UseAssignableRolesOptions
): UseAssignableRolesResult => {
	const { roles, isLoading, error } = useRoles(assignableToUserIds, {
		enabled: options?.enabled,
		excludeEventScoped: true,
	});

	const assignableRoles = useMemo(
		() => roles.filter((role) => role.is_event_scoped !== true),
		[roles]
	);

	return { roles: assignableRoles, isLoading, error };
};
